import { localeHeaders, translate } from "@/i18n";
import { authHeaders, expireSession } from "@/services/auth";

// The ONE way the screens talk to this app's API: the staff token and the chosen language on every
// request (the server answers in that language), and the
// server's own error message surfaced as the Error — a feed outage reads differently from a bug
// here, and the floor needs to know which one to chase.
export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: "application/json", ...localeHeaders(), ...authHeaders(), ...operatorHeaders(), ...(init?.headers || {}) },
  });
  if (res.status === 401) expireSession();
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // The status and the body travel with the message, for a screen that answers a refusal differently
    // (his Resume with nobody left on the job opens the picker, from the run the refusal carries).
    throw Object.assign(new Error((body as { error?: string }).error || translate("error.request", { status: res.status })), { status: res.status, body });
  }
  return res.json() as Promise<T>;
}

// His "Vous êtes :" — the first name typed on a shared tablet, sent so the server records who did it
// (attribution only; the account still decides what may be done).
export const OPERATOR_KEY = "myotaku-warehouse:operator";
export function readOperator() {
  try { return localStorage.getItem(OPERATOR_KEY) ?? ""; } catch { return ""; }
}
function operatorHeaders(): Record<string, string> {
  const name = readOperator().trim();
  // Header values are Latin-1: a name with other letters is sent encoded rather than dropped.
  return name ? { "X-Operator": /^[\x20-\xff]*$/.test(name) ? name : encodeURIComponent(name) } : {};
}

const withBody = <T,>(method: string, path: string, body?: unknown) =>
  call<T>(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });

export const post = <T,>(path: string, body?: unknown) => withBody<T>("POST", path, body);
export const patch = <T,>(path: string, body?: unknown) => withBody<T>("PATCH", path, body);
